import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiCheckCircle as CheckCircle,
  FiArrowRight as ArrowRight,
  FiX as X,
  FiChevronDown as ChevronDown,
  FiChevronUp as ChevronUp,
  FiDatabase,
  FiGitBranch,
  FiPlay
} from 'react-icons/fi';
import { useDataOpsOnboardingStatus, DATAOPS_ONBOARDING_STEPS, DATAOPS_SECTIONS } from '../../../hooks/useDataOpsOnboardingStatus';

type IconComponent = React.ComponentType<{ className?: string }>;

// Icon mapping for steps
const STEP_ICONS: Record<string, IconComponent> = {
  explore_catalog: FiDatabase,
  view_lineage: FiGitBranch,
  monitor_runs: FiPlay
};

interface DataOpsGettingStartedBannerProps {
  onDismiss?: () => void;
  showDismiss?: boolean;
}

/**
 * DataOps Getting Started Banner Component
 * Shows onboarding progress and guides users through the setup process
 */
const DataOpsGettingStartedBanner = ({ onDismiss, showDismiss = true }: DataOpsGettingStartedBannerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    stepStatusMap,
    completedSteps,
    totalSteps,
    completionPercentage,
    nextStep,
    isComplete,
    loading
  } = useDataOpsOnboardingStatus();

  // Don't show if onboarding is complete
  if (isComplete) {
    return null;
  }

  // Don't show while loading
  if (loading) {
    return null;
  }

  const getStepIcon = (stepId: string): IconComponent => {
    return STEP_ICONS[stepId] || FiDatabase;
  };

  return (
    <div className="bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-lg shadow-lg mb-6 overflow-hidden">
      {/* Header - Always visible */}
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0">
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="rgba(255,255,255,0.2)"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeDasharray={`${completionPercentage}, 100`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">
                  {completedSteps}/{totalSteps}
                </span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold">Getting Started with DataOps</h3>
              <p className="text-purple-100 text-sm">
                {nextStep ? `Next: ${nextStep.label}` : 'Complete your setup'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {nextStep && (
              <Link
                to={nextStep.route}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
              >
                Continue Setup
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </button>
            {showDismiss && onDismiss && (
              <button
                onClick={onDismiss}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                aria-label="Dismiss"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="pt-4 border-t border-white/20">
            {/* Progress Steps */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {DATAOPS_ONBOARDING_STEPS.map((step) => {
                const isCompleted = stepStatusMap[step.id];
                const isCurrent = nextStep?.id === step.id;
                const StepIcon = getStepIcon(step.id);

                let stepLinkClass: string;
                if (isCompleted) stepLinkClass = 'bg-white/20 hover:bg-white/30';
                else if (isCurrent) stepLinkClass = 'bg-white text-purple-600 shadow-lg';
                else stepLinkClass = 'bg-white/10 hover:bg-white/20';

                let stepIconClass: string;
                if (isCompleted) stepIconClass = 'text-green-300';
                else if (isCurrent) stepIconClass = 'text-purple-600';
                else stepIconClass = 'text-white/60';

                let stepTitleClass: string;
                if (isCurrent) stepTitleClass = 'text-gray-900';
                else if (isCompleted) stepTitleClass = 'text-white';
                else stepTitleClass = 'text-white/80';

                return (
                  <Link
                    key={step.id}
                    to={step.route}
                    className={`block p-4 rounded-lg transition-all ${stepLinkClass}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 ${stepIconClass}`}>
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <StepIcon className="w-5 h-5" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-xs uppercase tracking-wider mb-1 ${
                          isCurrent ? 'text-purple-400' : 'text-white/60'
                        }`}>
                          {DATAOPS_SECTIONS[step.section]}
                        </div>
                        <div className={`font-medium text-sm ${stepTitleClass}`}>
                          {step.label}
                        </div>
                        <div className={`text-xs mt-1 ${
                          isCurrent ? 'text-gray-600' : 'text-white/60'
                        }`}>
                          {step.description}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Journey Flow */}
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-center gap-2 text-xs text-purple-100">
                <span className="px-2 py-1 bg-white/20 rounded">Discover</span>
                <ArrowRight className="w-3 h-3" />
                <span className="px-2 py-1 bg-white/20 rounded">Understand</span>
                <ArrowRight className="w-3 h-3" />
                <span className="px-2 py-1 bg-white/20 rounded">Monitor</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Compact version of the banner for use in page headers
 */
export const DataOpsGettingStartedCompact = () => {
  const {
    completedSteps,
    totalSteps,
    nextStep,
    isComplete,
    loading
  } = useDataOpsOnboardingStatus();

  if (isComplete || loading) {
    return null;
  }

  return (
    <Link
      to={nextStep?.route || '/dataops/catalog'}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-sm hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
    >
      <div className="flex items-center gap-1.5">
        <span className="font-medium">{completedSteps}/{totalSteps}</span>
        <span className="text-purple-500 dark:text-purple-400">setup complete</span>
      </div>
      <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  );
};

export default DataOpsGettingStartedBanner;
